(() => {
  'use strict';

  if (window.__hanezzaProductRatingPositionLoaded) return;
  window.__hanezzaProductRatingPositionLoaded = true;

  const ROOT_SELECTOR = '#hanezza-product-scope';
  const GOLD = '#C89436';
  const RATING_LINK_SELECTOR = [
    'a[href="#testimonials"]',
    'a[href="#product-reviews"]',
    'a[href="#reviews"]',
    'a[href$="#testimonials"]',
    'a[href$="#product-reviews"]',
    'a[href$="#reviews"]'
  ].join(',');

  let observer = null;
  let scheduledFrame = 0;
  let lastError = null;
  let lastMode = null;

  function isVisible(element) {
    if (!element || !element.isConnected) return false;
    if (element.closest('#hanezza-rating-debug-overlay')) return false;

    let current = element;
    while (current && current !== document.documentElement) {
      const style = window.getComputedStyle(current);
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
      current = current.parentElement;
    }

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function hasStars(element) {
    return Boolean(element && element.querySelectorAll('svg').length >= 4);
  }

  function looksLikeRating(element) {
    if (!hasStars(element)) return false;
    const text = (element.textContent || '').toLocaleLowerCase('tr-TR');
    return /review|yorum|değerlendirme/.test(text);
  }

  function isMobileNode(element) {
    return Boolean(element && element.closest('.custom-mobile-view'));
  }

  function isSafeCandidate(candidate, title, reference) {
    if (!candidate || !title || !reference) return false;
    if (candidate === title || candidate === reference) return false;
    if (candidate.contains(title) || candidate.contains(reference)) return false;
    if (title.contains(candidate) || reference.contains(candidate)) return false;
    return true;
  }

  function getStarWrap(rating) {
    if (!rating) return null;
    return Array.from(rating.children || []).find(hasStars)
      || Array.from(rating.querySelectorAll('div, span')).find(hasStars)
      || rating;
  }

  function paintStars(rating) {
    const starWrap = getStarWrap(rating);
    if (!starWrap) return false;

    starWrap.style.setProperty('color', GOLD, 'important');
    starWrap.querySelectorAll('svg').forEach((svg) => {
      svg.style.setProperty('color', GOLD, 'important');
      svg.style.setProperty('fill', 'currentColor', 'important');
    });
    starWrap.querySelectorAll('svg *').forEach((element) => {
      element.style.setProperty('color', GOLD, 'important');
      if (element.getAttribute('fill') !== 'none') {
        element.style.setProperty('fill', 'currentColor', 'important');
      }
      if (element.hasAttribute('stroke') && element.getAttribute('stroke') !== 'none') {
        element.style.setProperty('stroke', 'currentColor', 'important');
      }
    });

    return true;
  }

  function localizeReviewLabel(rating) {
    if (!rating) return false;

    let changed = false;
    const walker = document.createTreeWalker(rating, NodeFilter.SHOW_TEXT);
    const textNodes = [];

    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((node) => {
      const current = node.nodeValue || '';
      const localized = current.replace(/\breviews?\b/gi, 'Müşteri Yorumu');
      if (localized !== current) {
        node.nodeValue = localized;
        changed = true;
      }
    });

    ['aria-label', 'title'].forEach((attribute) => {
      const current = rating.getAttribute(attribute);
      if (!current) return;
      const localized = current.replace(/\breviews?\b/gi, 'Müşteri Yorumu');
      if (localized !== current) {
        rating.setAttribute(attribute, localized);
        changed = true;
      }
    });

    return changed;
  }

  function scoreTitle(title, mobile) {
    let score = 0;
    if (!title || !isVisible(title)) return -Infinity;

    if (mobile) {
      if (!isMobileNode(title)) return -Infinity;
      score += 500;
    } else {
      if (isMobileNode(title)) return -Infinity;
      if (title.closest('#product-details-container')) score += 1000;
      if (title.closest('.custom-desktop-view')) score += 900;
      if (title.closest('#tt-pageContent')) score += 200;
    }

    const textLength = (title.textContent || '').trim().length;
    if (textLength > 8) score += Math.min(textLength, 120);
    return score;
  }

  function findTitle(mobile) {
    const candidates = Array.from(document.querySelectorAll('h1'));
    return candidates
      .map((title) => ({ title, score: scoreTitle(title, mobile) }))
      .filter((item) => Number.isFinite(item.score))
      .sort((a, b) => b.score - a.score)[0]?.title || null;
  }

  function collectRatingCandidates() {
    const exactLinks = Array.from(document.querySelectorAll(RATING_LINK_SELECTOR));
    const semantic = Array.from(document.querySelectorAll('a, div, span')).filter((element) => {
      if (!looksLikeRating(element)) return false;
      return !Array.from(element.children || []).some((child) => looksLikeRating(child));
    });

    return Array.from(new Set([...exactLinks, ...semantic]));
  }

  function scoreRating(candidate, title, reference, mobile) {
    if (!candidate || !isVisible(candidate)) return -Infinity;
    if (!isSafeCandidate(candidate, title, reference)) return -Infinity;
    if (mobile !== isMobileNode(candidate)) return -Infinity;
    if (!hasStars(candidate)) return -Infinity;

    let score = 0;
    const href = candidate.getAttribute?.('href') || '';
    if (/#(?:testimonials|product-reviews|reviews)$/.test(href)) score += 700;
    if (looksLikeRating(candidate)) score += 500;
    if (candidate.parentElement === title.parentElement) score += 450;

    const titleContainer = title.closest('#product-details-container, .custom-desktop-view, .custom-mobile-view, #tt-pageContent');
    const ratingContainer = candidate.closest('#product-details-container, .custom-desktop-view, .custom-mobile-view, #tt-pageContent');
    if (titleContainer && titleContainer === ratingContainer) score += 300;

    const titleRect = title.getBoundingClientRect();
    const ratingRect = candidate.getBoundingClientRect();
    score -= Math.min(Math.abs(ratingRect.top - titleRect.bottom), 2000) / 4;

    return score;
  }

  function findRating(title, reference, mobile) {
    return Array.from(document.querySelectorAll('[data-hanezza-product-rating]'))
      .find((candidate) => (
        isVisible(candidate)
        && isSafeCandidate(candidate, title, reference)
        && mobile === isMobileNode(candidate)
        && hasStars(candidate)
      )) || null;
  }

  function getReference(title, mobile) {
    if (!title) return null;
    if (!mobile) return title;

    const parent = title.parentElement;
    if (!parent) return title;
    return parent.querySelector('button, a') ? parent : title;
  }

  function safePosition(reference, rating, mobile) {
    if (!reference || !rating) return false;
    if (rating.contains(reference) || reference.contains(rating)) {
      lastError = 'unsafe-ancestor-candidate';
      return false;
    }

    try {
      if (reference.nextElementSibling !== rating) {
        reference.insertAdjacentElement('afterend', rating);
      }
    } catch (error) {
      lastError = `${error.name}: ${error.message}`;
      return false;
    }

    rating.classList.add('hanezza-product-rating-after-title');
    rating.style.setProperty('display', 'flex', 'important');
    rating.style.setProperty('align-items', 'center', 'important');
    rating.style.setProperty('align-self', 'flex-start', 'important');
    rating.style.setProperty('width', 'max-content', 'important');
    rating.style.setProperty('order', 'initial', 'important');
    rating.style.setProperty('margin', mobile ? '4px 0 12px' : '0 0 14px', 'important');
    localizeReviewLabel(rating);
    paintStars(rating);
    return true;
  }

  function resolveContext(mobile) {
    const title = findTitle(mobile);
    const reference = getReference(title, mobile);
    const rating = title && reference ? findRating(title, reference, mobile) : null;
    return { title, reference, rating };
  }

  function apply() {
    const root = document.querySelector(ROOT_SELECTOR);
    const mobile = !window.matchMedia('(min-width: 1024px)').matches;
    const context = resolveContext(mobile);

    lastError = null;
    lastMode = mobile ? 'mobile' : 'desktop';
    const applied = safePosition(context.reference, context.rating, mobile);

    if (root) {
      root.dataset.hanezzaRatingPosition = applied ? 'after-title' : 'waiting';
      root.dataset.hanezzaRatingGold = GOLD;
      root.dataset.hanezzaRatingMode = lastMode;
      if (lastError) root.dataset.hanezzaRatingError = lastError;
      else delete root.dataset.hanezzaRatingError;
    }

    return applied;
  }

  function scheduleApply() {
    cancelAnimationFrame(scheduledFrame);
    scheduledFrame = requestAnimationFrame(apply);
  }

  function start() {
    apply();

    if (document.body && !observer) {
      observer = new MutationObserver(scheduleApply);
      observer.observe(document.body, { childList: true, subtree: true });
    }

    [100, 300, 750, 1500, 3000, 5000].forEach((delay) => window.setTimeout(apply, delay));
  }

  window.HanezzaProductRatingPosition = {
    apply,
    status() {
      const root = document.querySelector(ROOT_SELECTOR);
      const mobile = !window.matchMedia('(min-width: 1024px)').matches;
      const active = resolveContext(mobile);
      const desktop = resolveContext(false);
      const mobileContext = resolveContext(true);
      const visibleHeadings = Array.from(document.querySelectorAll('h1')).filter(isVisible);
      const visibleRatings = collectRatingCandidates().filter(isVisible);

      return {
        loaded: true,
        state: root?.dataset.hanezzaRatingPosition || (active.title && active.rating ? 'ready' : 'waiting'),
        gold: GOLD,
        viewportWidth: window.innerWidth,
        activeMode: mobile ? 'mobile' : 'desktop',
        activeTitle: Boolean(active.title),
        activeRating: Boolean(active.rating),
        desktopTitle: Boolean(desktop.title),
        desktopRating: Boolean(desktop.rating),
        mobileTitle: Boolean(mobileContext.title),
        mobileRating: Boolean(mobileContext.rating),
        documentHeadings: document.querySelectorAll('h1').length,
        visibleHeadings: visibleHeadings.length,
        documentRatingLinks: document.querySelectorAll(RATING_LINK_SELECTOR).length,
        visibleRatingCandidates: visibleRatings.length,
        rootContainsActiveTitle: Boolean(root && active.title && root.contains(active.title)),
        rootContainsActiveRating: Boolean(root && active.rating && root.contains(active.rating)),
        lastMode,
        lastError: root?.dataset.hanezzaRatingError || lastError
      };
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('load', scheduleApply, { once: true });
  window.addEventListener('pageshow', scheduleApply);
  window.addEventListener('resize', scheduleApply);
  document.addEventListener('shopify:section:load', scheduleApply);
})();